import { useState, useEffect, useCallback } from "react";
import { useTheme } from "@/lib/ThemeContext";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Check, X, UserPlus, Shield } from "lucide-react";
import logger from "@/lib/logger";

export default function InvitesPanel() {
  const { tokens: t } = useTheme();
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);

  const fetchInvites = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.getMyPendingInvites();
      setInvites(res.invitations || []);
    } catch (e) { logger.error("fetchInvites:", e); }
  }, [user]);

  useEffect(() => {
    fetchInvites();
    const id = setInterval(fetchInvites, 60000);
    return () => clearInterval(id);
  }, [fetchInvites]);

  const accept = async (id) => {
    try {
      await api.acceptTeamInvite(id);
      toast.success("Invite accepted!");
      fetchInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept");
    }
  };

  const decline = async (id) => {
    try {
      await api.declineTeamInvite(id);
      toast.success("Invite declined");
      fetchInvites();
    } catch {
      toast.error("Failed to decline");
    }
  };

  if (!user || invites.length === 0) return null;

  return (
    <div
      className="fixed bottom-6 left-6 z-[9998] w-80 rounded-xl border shadow-2xl"
      style={{ background: t.bgSecondary, borderColor: t.borderColor }}
      data-testid="invites-panel"
    >
      <div
        className="flex items-center gap-2 px-3 py-2 border-b"
        style={{ borderColor: t.borderColor }}
      >
        <UserPlus className="h-3.5 w-3.5" style={{ color: t.accent }} />
        <span className="text-[11px] font-semibold" style={{ color: t.textPrimary }}>
          Team Invites ({invites.length})
        </span>
      </div>
      <div className="max-h-48 overflow-y-auto">
        {invites.map((inv) => (
          <div
            key={inv.id}
            className="px-3 py-2.5 border-b last:border-0"
            style={{ borderColor: `${t.borderColor}50` }}
            data-testid={`team-invite-${inv.id}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Mail className="h-3 w-3" style={{ color: t.accent }} />
              <span className="text-[11px] font-medium truncate" style={{ color: t.textPrimary }}>
                {inv.project_name}
              </span>
            </div>
            <div className="flex items-center gap-1 mb-2">
              <Shield className="h-2.5 w-2.5" style={{ color: t.textMuted }} />
              <p className="text-[9px]" style={{ color: t.textMuted }}>
                From {inv.invited_by_name} &middot; Role: {inv.role}
              </p>
            </div>
            <div className="flex gap-1.5">
              <button
                onClick={() => accept(inv.id)}
                className="flex items-center gap-1 text-[10px] font-semibold px-3 py-1 rounded-lg"
                style={{ background: t.accent, color: t.bgPrimary }}
                data-testid={`accept-team-invite-${inv.id}`}
              >
                <Check className="h-3 w-3" /> Accept
              </button>
              <button
                onClick={() => decline(inv.id)}
                className="flex items-center gap-1 text-[10px] font-semibold px-3 py-1 rounded-lg"
                style={{ background: `${t.textMuted}15`, color: t.textMuted }}
                data-testid={`decline-team-invite-${inv.id}`}
              >
                <X className="h-3 w-3" /> Decline
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
